<script lang="ts" setup>
import { ErrorStatus } from '~/constants'
import { useAuthStore } from '~/stores/auth'
import { useForgotPasswordSendMail } from '~/composables/form/use-forgot-password'
import { useModalsStore } from '#lib/stores'
import { RoutesEnum } from '~/enums'
import { useCustomLiveChat } from '~/composables/use-custom-live-chat'

const { navigate } = useNavigate()
const authStore = useAuthStore()
const forgotPasswordStore = useForgotPasswordStore()
const { onForgotPassword } = useAccount()
const { isMobile } = useDevice()
const { openLiveChat } = useCustomLiveChat()
const { setOpenAuthModal } = useModalsStore()

const isLoading = ref(false)
const sendMail = ref(false)

const FIVE_MINUTES = 5 * 60 * 1000

const openLoginForm = () => {
  authStore.forgotPasswordStepSendMail = false
  setOpenAuthModal(true)
}

const { values, meta, isSubmitting, handleSubmit, setErrors } =
  useForgotPasswordSendMail()
const disableBtn = computed(
  () =>
    sendMail.value ||
    !meta.value.dirty ||
    !meta.value.valid ||
    isLoading.value ||
    isSubmitting.value,
)
const loading = computed(() => isLoading.value || isSubmitting.value)

const errorMessage = ref<string | undefined>(undefined)
const showError = ref<boolean>(false)
const showSuccess = ref<boolean>(false)

const onClearError = () => {
  errorMessage.value = undefined
}

const onOpenErrorModal = (message: string) => {
  authStore.forgotPasswordStepSendMail = false
  errorMessage.value = message
  showError.value = true
}

const onRetry = () => {
  showError.value = false
  errorMessage.value = undefined
  authStore.forgotPasswordStepSendMail = true
}

const onSuccess = () => {
  authStore.forgotPasswordStepSendMail = false
  showSuccess.value = true
}

const onBackToHome = () => {
  authStore.forgotPasswordStepSendMail = false
  navigate(RoutesEnum.Home)
}

const validateSentTime = () => {
  const currentTime = new Date().getTime()
  const diff = currentTime - forgotPasswordStore.lastSendTime
  return diff >= FIVE_MINUTES
}

watch(
  () => values.email,
  () => {
    sendMail.value = false
  },
)

const onSubmit = handleSubmit(async (formData: { email: string }) => {
  try {
    const validateTime = validateSentTime()
    if (!validateTime) {
      sendMail.value = true
      return
    }

    isLoading.value = true
    await onForgotPassword(formData.email)
    isLoading.value = false

    forgotPasswordStore.updateLastSendTime()
    onSuccess()
  } catch (ex) {
    const status = (ex as { status: ErrorStatus }).status
    isLoading.value = false
    switch (status) {
      case ErrorStatus.EmailNotFoundError:
        setErrors({ email: 'Email không tồn tại. Quý khách vui lòng thử lại' })
        break
      case ErrorStatus.LimitRequestEmail:
        onOpenErrorModal(
          (ex as { message: string }).message?.replaceAll(',', ', \n'),
        )
        break
      default:
        onOpenErrorModal('Không thể gửi email, xin vui lòng kiểm tra lại.')
        break
    }
  } finally {
    isLoading.value = false
  }
})
</script>

<template>
  <div>
    <BaseModal
      v-model="authStore.forgotPasswordStepSendMail"
      :title="`Quên mật khẩu`"
      custom-class="forgot-password-modal"
    >
      <form
        :class="{
          'form-forgot-password': true,
          'form-forgot-password--mobile': isMobile,
        }"
        action="#"
        @submit.prevent="onSubmit"
      >
        <FormInput
          name="email"
          placeholder="Nhập địa chỉ email của bạn"
          label="Email"
          :max-length="50"
          @input="onClearError"
        />
        <BaseWarningMessage v-if="sendMail" class="sent-warning-message">
          <template #message>
            Bạn đã gửi yêu cầu đặt lại mật khẩu, trường hợp chưa nhận được
            email, vui lòng gửi lại yêu cầu sau 5 phút.
          </template>
        </BaseWarningMessage>
        <div class="form-forgot-password__button-group">
          <BaseButton
            custom-class="goback-btn icon-left"
            variant="outline"
            button-id="open-login-form"
            button-name="open-login-form"
            @click="openLoginForm"
          />
          <BaseButton
            custom-class="submit-btn"
            type="submit"
            variant="primary"
            button-id="reset-password"
            button-name="reset-password"
            :disabled="disableBtn"
            :loading="loading"
            label="Đặt lại mật khẩu"
          />
        </div>
        <p class="form-forgot-password__cskh">
          Bạn không có email ?
          <span class="text-underline" @click.prevent="openLiveChat()"
            >Liên hệ CSKH</span
          >
        </p>
      </form>
    </BaseModal>
    <BaseConfirm
      v-model="showError"
      title="Thất bại"
      :confirm-label="`Thử lại`"
      image="/images/popup/failed.svg"
      @on-confirm="onRetry"
      @on-button-close="onBackToHome"
    >
      <template #description>
        <span class="whitespace-pre-wrap">{{ errorMessage }}</span>
      </template>
    </BaseConfirm>
    <BaseConfirm
      v-model="showSuccess"
      :timeout="2000"
      title="Thành công"
      description="Gửi email thành công, xin vui lòng kiểm tra."
      image="/images/popup/success.svg"
    />
  </div>
</template>
<style
  lang="scss"
  src="/assets/styles/components/desktop/form/forgot-password-send-email.scss"
  scoped
></style>
